import * as React from "react";
import {
  LoadingSpinner,
  PrimaryBlueButton,
  SecondaryBlueButton,
  Severity,
  Toggle
} from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import FormGroup from "@/components/FormGroup";

interface TenantStatus {
  site_id: number;
  site_info: any;
  tenant: any;
  subscription: any;
  v10_subscription: any;
  actions_taken: string[];
  status: string;
}

interface KBInfo {
  configured: boolean;
  hasTenant?: boolean;
  tenant?: {
    Id: string;
    CompanyName: string;
    Domain: string;
    ThemeColor: string;
    EnableLiveChat: boolean;
    LogoUrl: string;
  };
  stats?: {
    articles: number;
    topics: number;
    categories: number;
  };
  publicUrl?: string;
  domainSetup?: {
    status: string;
    instructions: string | null;
  };
  message?: string;
  error?: boolean;
}

interface KBConnectionInfo {
  configured: boolean;
  current?: {
    server: string;
    database: string;
    isAlternate: boolean;
  };
  primary?: {
    server: string;
    database: string;
  };
  alternate?: {
    server: string;
    database: string;
  } | null;
  hasAlternate?: boolean;
  environment?: string;
  error?: string;
}

interface KBMigrationCounts {
  configured: boolean;
  counts?: {
    tenants: number;
    categories: number;
    topics: number;
    articles: number;
    hits: number;
    votes: number;
  };
  error?: string;
}

interface AuthModeConfig {
  siteId: number;
  authMode: string;
  allowLocalLogin: boolean;
  migratedAt?: string;
  migratedBy?: string;
  hasConfig: boolean;
  error?: string;
}

interface MigrationStep {
  id: string;
  label: string;
  detail?: string;
}

const MIGRATION_STEPS: MigrationStep[] = [
  { id: "prod-db", label: "Create production KB database (kb_v20)" },
  { id: "prod-schema", label: "Create tables via EF Core migration" },
  { id: "prod-data", label: "Migrate data from old kb to kb_v20" },
  { id: "prod-verify", label: "Verify row counts match" },
  { id: "prod-connstr", label: "Update production connection string to kb_v20" },
  { id: "reset-password", label: "Reset SQL admin password (rotated during migration)" },
  { id: "remove-firewall", label: "Remove temp firewall rule on kb-velaro-prod" },
  { id: "deploy-v20", label: "Deploy v20 to production" },
  {
    id: "add-domain",
    label: "Add help.velaro.com custom domain to prod app service",
    detail:
      "Azure Portal → velaro-admin-prod → Custom domains → Add custom domain → help.velaro.com"
  },
  {
    id: "update-dns",
    label: "Update DNS CNAME for help.velaro.com",
    detail:
      "EasyDNS → CNAME: help → velaro-admin-prod.azurewebsites.net"
  },
  {
    id: "verify-live",
    label: "Verify https://help.velaro.com/kb/ serves from v20"
  }
];

const STORAGE_KEY = "kb-migration-checklist";

function loadChecklist(): Record<string, boolean> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  // Default: first 5 steps are done (completed during migration)
  return {
    "prod-db": true,
    "prod-schema": true,
    "prod-data": true,
    "prod-verify": true,
    "prod-connstr": true
  };
}

function saveChecklist(checklist: Record<string, boolean>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(checklist));
  } catch {}
}

export default function DatabaseTool() {
  const api = useApi();
  const { displayToast } = useToastAlert();

  const [siteId, setSiteId] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [status, setStatus] = React.useState<TenantStatus | null>(null);
  const [kbInfo, setKbInfo] = React.useState<KBInfo | null>(null);
  const [kbLoading, setKbLoading] = React.useState(false);
  const [kbDomain, setKbDomain] = React.useState("");
  const [kbDomainSaving, setKbDomainSaving] = React.useState(false);

  // Auth mode state
  const [authModeConfig, setAuthModeConfig] = React.useState<AuthModeConfig | null>(null);
  const [authModeLoading, setAuthModeLoading] = React.useState(false);
  const [authModeSaving, setAuthModeSaving] = React.useState(false);
  const [selectedAuthMode, setSelectedAuthMode] = React.useState("V10");
  const [selectedAllowLocal, setSelectedAllowLocal] = React.useState(true);

  // Migration panel state
  const [connInfo, setConnInfo] = React.useState<KBConnectionInfo | null>(null);
  const [connLoading, setConnLoading] = React.useState(false);
  const [migrationCounts, setMigrationCounts] = React.useState<KBMigrationCounts | null>(null);
  const [checklist, setChecklist] = React.useState<Record<string, boolean>>(loadChecklist);
  const [swapping, setSwapping] = React.useState(false);

  async function checkAndFixSite() {
    if (!siteId) {
      displayToast(Severity.Error, "Please enter a Site ID");
      return;
    }

    setLoading(true);
    try {
      const response = await api.admin.post("DatabaseTool/AutoVerify", {
        siteId: parseInt(siteId)
      });

      const data = await response.json();
      setStatus(data);

      if (data.actions_taken && data.actions_taken.length > 0) {
        const hasChanges = data.actions_taken.some(
          (action: string) =>
            action.includes("Created") || action.includes("✅")
        );

        if (hasChanges) {
          displayToast(Severity.Success, "Site configured successfully!");
        } else {
          displayToast(Severity.Info, "Site already configured correctly");
        }
      }
    } catch (error: any) {
      displayToast(Severity.Error, `Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function toggleFeature(feature: string, enabled: boolean) {
    if (!siteId) return;

    try {
      await api.admin.post("DatabaseTool/ToggleFeature", {
        siteId: parseInt(siteId),
        feature,
        enabled
      });

      displayToast(
        Severity.Success,
        `${feature} ${enabled ? "enabled" : "disabled"}`
      );

      // Refresh status
      checkAndFixSite();
    } catch (error: any) {
      displayToast(Severity.Error, `Error: ${error.message}`);
    }
  }

  async function fetchKBInfo() {
    if (!siteId) return;
    setKbLoading(true);
    try {
      const response = await api.admin.get(
        `DatabaseTool/KBInfo?siteId=${siteId}`
      );
      const data = await response.json();
      setKbInfo(data);
      setKbDomain(data?.tenant?.Domain ?? "");
    } catch (error: any) {
      console.error("Failed to fetch KB info:", error);
    } finally {
      setKbLoading(false);
    }
  }

  async function fetchAuthMode() {
    if (!siteId) return;
    setAuthModeLoading(true);
    try {
      const response = await api.admin.get(`DatabaseTool/AuthMode/${siteId}`);
      const data = await response.json();
      setAuthModeConfig(data);
      setSelectedAuthMode(data.authMode ?? "V10");
      setSelectedAllowLocal(data.allowLocalLogin ?? true);
    } catch (error: any) {
      console.error("Failed to fetch auth mode:", error);
    } finally {
      setAuthModeLoading(false);
    }
  }

  async function saveAuthMode() {
    if (!siteId) return;
    setAuthModeSaving(true);
    try {
      await api.admin.put(`DatabaseTool/AuthMode/${siteId}`, {
        authMode: selectedAuthMode,
        allowLocalLogin: selectedAllowLocal
      });
      displayToast(Severity.Success, `Auth mode set to ${selectedAuthMode}`);
      fetchAuthMode();
    } catch (error: any) {
      displayToast(Severity.Error, `Error: ${error.message}`);
    } finally {
      setAuthModeSaving(false);
    }
  }

  async function saveKBDomain() {
    if (!kbInfo?.tenant?.Id) return;
    setKbDomainSaving(true);
    try {
      await api.admin.post("DatabaseTool/KBSetDomain", {
        tenantId: kbInfo.tenant.Id,
        domain: kbDomain
      });
      displayToast(Severity.Success, "KB domain updated");
      fetchKBInfo();
    } catch (error: any) {
      displayToast(Severity.Error, `Error: ${error.message}`);
    } finally {
      setKbDomainSaving(false);
    }
  }

  async function fetchConnectionInfo() {
    setConnLoading(true);
    try {
      const response = await api.admin.get("DatabaseTool/KBConnectionInfo");
      const data = await response.json();
      setConnInfo(data);
    } catch (error: any) {
      console.error("Failed to fetch KB connection info:", error);
    } finally {
      setConnLoading(false);
    }
  }

  async function fetchMigrationCounts() {
    try {
      const response = await api.admin.get("DatabaseTool/KBMigrationCounts");
      const data = await response.json();
      setMigrationCounts(data);
    } catch (error: any) {
      console.error("Failed to fetch migration counts:", error);
    }
  }

  async function swapConnection(useAlternate: boolean) {
    setSwapping(true);
    try {
      const response = await api.admin.post("DatabaseTool/KBSwapConnection", {
        useAlternate
      });
      const data = await response.json();
      if (data.success) {
        displayToast(
          Severity.Success,
          `Switched to ${data.current?.database} on ${data.current?.server}`
        );
        fetchConnectionInfo();
        fetchMigrationCounts();
      }
    } catch (error: any) {
      displayToast(Severity.Error, `Swap failed: ${error.message}`);
    } finally {
      setSwapping(false);
    }
  }

  function toggleStep(stepId: string) {
    const updated = { ...checklist, [stepId]: !checklist[stepId] };
    setChecklist(updated);
    saveChecklist(updated);
  }

  // Fetch KB info and auth mode when site status is loaded
  React.useEffect(() => {
    if (status && siteId) {
      fetchKBInfo();
      fetchAuthMode();
    }
  }, [status]);

  // Fetch connection info on mount
  React.useEffect(() => {
    fetchConnectionInfo();
    fetchMigrationCounts();
  }, []);

  const features = [
    { key: "EnableWeb", label: "Web Messenger" },
    { key: "EnableSms", label: "SMS Channel" },
    { key: "EnableEmail", label: "Email Channel" },
    { key: "EnableWhatsapp", label: "WhatsApp" },
    { key: "EnableFacebook", label: "Facebook Messenger" },
    { key: "EnableNetsuite", label: "NetSuite Integration" },
    { key: "EnableAutomation", label: "Automation" },
    { key: "EnableAI", label: "AI Chatbots" },
    { key: "EnableWorkflowRules", label: "Workflow Rules" },
    { key: "EnableMultipleDeployments", label: "Multiple Deployments" }
  ];

  const completedSteps = MIGRATION_STEPS.filter((s) => checklist[s.id]).length;
  const totalSteps = MIGRATION_STEPS.length;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 mb-2">
          Database Administration Tool
        </h1>
        <p className="text-slate-600">
          Verify and configure messaging tenant and subscription settings for
          any site
        </p>
      </div>

      {/* Site ID Input */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <FormGroup label="Site ID">
          <div className="flex gap-4">
            <input
              type="number"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              placeholder="Enter site ID to check"
              className="flex-1 px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <PrimaryBlueButton
              label={loading ? "Checking..." : "Check & Fix"}
              onClick={checkAndFixSite}
              disabled={loading || !siteId}
            />
          </div>
        </FormGroup>

        {loading && (
          <div className="mt-4 flex justify-center">
            <LoadingSpinner />
          </div>
        )}
      </div>

      {/* Status Display */}
      {status && !loading && (
        <>
          {/* Actions Taken */}
          {status.actions_taken && status.actions_taken.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">
                Actions Taken:
              </h3>
              <ul className="list-disc list-inside space-y-1">
                {status.actions_taken.map((action, i) => (
                  <li key={i} className="text-blue-800">
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Site Info */}
            <div
              className={`rounded-lg p-4 border-2 ${
                status.site_info
                  ? "bg-green-50 border-green-500"
                  : "bg-red-50 border-red-500"
              }`}
            >
              <h3 className="font-semibold mb-2">Site Information</h3>
              <div
                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-2 ${
                  status.site_info
                    ? "bg-green-500 text-white"
                    : "bg-red-500 text-white"
                }`}
              >
                {status.site_info ? "Found" : "Not Found"}
              </div>
              {status.site_info && (
                <div className="text-sm space-y-1">
                  <div>
                    <strong>Company:</strong> {status.site_info.CompanyName}
                  </div>
                  <div>
                    <strong>Created:</strong>{" "}
                    {new Date(status.site_info.DateCreated).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>

            {/* Tenant */}
            <div
              className={`rounded-lg p-4 border-2 ${
                status.tenant
                  ? "bg-green-50 border-green-500"
                  : "bg-red-50 border-red-500"
              }`}
            >
              <h3 className="font-semibold mb-2">Messaging Tenant</h3>
              <div
                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-2 ${
                  status.tenant
                    ? "bg-green-500 text-white"
                    : "bg-red-500 text-white"
                }`}
              >
                {status.tenant ? "Exists" : "Missing"}
              </div>
              {status.tenant && (
                <div className="text-sm">
                  <div>
                    <strong>Tenant ID:</strong> {status.tenant.Id}
                  </div>
                </div>
              )}
            </div>

            {/* Subscription */}
            <div
              className={`rounded-lg p-4 border-2 ${
                status.subscription
                  ? "bg-green-50 border-green-500"
                  : "bg-red-50 border-red-500"
              }`}
            >
              <h3 className="font-semibold mb-2">Subscription</h3>
              <div
                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-2 ${
                  status.subscription
                    ? "bg-green-500 text-white"
                    : "bg-red-500 text-white"
                }`}
              >
                {status.subscription ? "Exists" : "Missing"}
              </div>
              {status.subscription && (
                <div className="text-sm">
                  <div>
                    <strong>Sub ID:</strong> {status.subscription.Id}
                  </div>
                </div>
              )}
            </div>

            {/* V10 License */}
            <div
              className={`rounded-lg p-4 border-2 ${
                status.v10_subscription?.Status === "Active"
                  ? "bg-green-50 border-green-500"
                  : "bg-yellow-50 border-yellow-500"
              }`}
            >
              <h3 className="font-semibold mb-2">V10 License</h3>
              <div
                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-2 ${
                  status.v10_subscription?.Status === "Active"
                    ? "bg-green-500 text-white"
                    : "bg-yellow-500 text-white"
                }`}
              >
                {status.v10_subscription?.Status || "Not Found"}
              </div>
              {status.v10_subscription && (
                <div className="text-sm">
                  <div>
                    <strong>Expires:</strong>{" "}
                    {new Date(
                      status.v10_subscription.ExpirationDate
                    ).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Feature Toggles */}
          {status.subscription && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-4">
                Feature Flags
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {features.map((feature) => (
                  <div
                    key={feature.key}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-md"
                  >
                    <span className="text-slate-700">{feature.label}</span>
                    <Toggle
                      toggled={status.subscription[feature.key] === 1}
                      changeToggle={() =>
                        toggleFeature(
                          feature.key,
                          status.subscription[feature.key] !== 1
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Auth Mode Section (per-site) */}
      {status && !loading && (
        <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
          <h2 className="text-xl font-bold text-slate-800 mb-1 flex items-center gap-2">
            <i className="fa fa-lock text-indigo-500" />
            Auth Mode (Messaging App)
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            Controls how agents log in to the messaging/workspace app. The admin app always uses Microsoft (Entra) login.
          </p>

          {authModeLoading ? (
            <div className="flex justify-center py-4"><LoadingSpinner /></div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { value: "V10", label: "Password Only", desc: "Email + password via V10 (legacy)", color: "slate" },
                  { value: "Dual", label: "Both Options", desc: "Agents can use password OR Microsoft login", color: "blue" },
                  { value: "EntraId", label: "Microsoft Only", desc: "Entra / Microsoft login required — no password", color: "indigo" }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSelectedAuthMode(opt.value)}
                    className={`text-left p-4 rounded-lg border-2 transition-all ${
                      selectedAuthMode === opt.value
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="font-semibold text-slate-800 text-sm">{opt.label}</div>
                    <div className="text-xs text-slate-500 mt-1">{opt.desc}</div>
                    {authModeConfig?.authMode === opt.value && (
                      <div className="text-xs text-green-600 font-medium mt-1">Currently active</div>
                    )}
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between p-3 border border-slate-200 rounded-md">
                <div>
                  <span className="text-slate-700 text-sm font-medium">Allow local password login</span>
                  <div className="text-xs text-slate-400">When off, only Microsoft/SSO login is accepted even in Dual mode</div>
                </div>
                <Toggle
                  toggled={selectedAllowLocal}
                  changeToggle={() => setSelectedAllowLocal(!selectedAllowLocal)}
                />
              </div>

              {authModeConfig?.migratedAt && (
                <div className="text-xs text-slate-400">
                  Migrated to EntraId on {new Date(authModeConfig.migratedAt).toLocaleDateString()} by {authModeConfig.migratedBy}
                </div>
              )}

              <div className="flex items-center gap-3">
                <PrimaryBlueButton
                  label={authModeSaving ? "Saving..." : "Save Auth Mode"}
                  onClick={saveAuthMode}
                  disabled={authModeSaving}
                />
                {authModeConfig && (
                  <span className="text-xs text-slate-400">
                    Current: <strong>{authModeConfig.authMode}</strong>
                    {!authModeConfig.hasConfig && " (default — not set)"}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* KB Domain & Info Section (per-site) */}
      {status && !loading && kbInfo && (
        <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
          <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-book text-blue-500" />
            Knowledge Base
          </h2>

          {kbLoading ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner />
            </div>
          ) : !kbInfo.configured ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                {kbInfo.message ||
                  "KB database not configured on this environment."}
              </p>
            </div>
          ) : !kbInfo.hasTenant ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                {kbInfo.message ||
                  "No KB tenant for this site. Customer needs to visit /kb/setup in admin."}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Tenant Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Company</div>
                  <div className="text-sm font-medium">
                    {kbInfo.tenant?.CompanyName ?? "\u2014"}
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Tenant ID</div>
                  <div className="text-xs font-mono break-all">
                    {kbInfo.tenant?.Id ?? "\u2014"}
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Content</div>
                  <div className="text-sm font-medium">
                    {kbInfo.stats?.articles ?? 0} articles,{" "}
                    {kbInfo.stats?.topics ?? 0} topics,{" "}
                    {kbInfo.stats?.categories ?? 0} categories
                  </div>
                </div>
              </div>

              {/* Domain Setup */}
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <i className="fa fa-globe text-green-500" />
                  Custom Domain
                </h3>

                {kbInfo.tenant?.Domain ? (
                  <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                      <span className="text-sm font-medium text-green-800">
                        Active:
                      </span>
                      <a
                        href={`https://${kbInfo.tenant.Domain}/kb/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-green-700 hover:underline"
                      >
                        https://{kbInfo.tenant.Domain}/kb/
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      No custom domain configured. KB is accessible via tenant
                      GUID URL only.
                    </p>
                  </div>
                )}

                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="block text-xs text-slate-500 mb-1">
                      Domain (e.g., help.acme.com)
                    </label>
                    <input
                      type="text"
                      value={kbDomain}
                      onChange={(e) => setKbDomain(e.target.value)}
                      placeholder="help.customer.com"
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <PrimaryBlueButton
                    label={kbDomainSaving ? "Saving..." : "Set Domain"}
                    onClick={saveKBDomain}
                    disabled={kbDomainSaving || !kbDomain}
                  />
                </div>

                {/* Setup Instructions */}
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-xs font-semibold text-blue-900 mb-2">
                    Domain Setup Checklist
                  </h4>
                  <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                    <li>
                      Customer creates CNAME:{" "}
                      <code className="bg-blue-100 px-1 rounded">
                        help.customer.com
                      </code>{" "}
                      &rarr;{" "}
                      <code className="bg-blue-100 px-1 rounded">
                        velaro-admin-prod.azurewebsites.net
                      </code>
                    </li>
                    <li>
                      Customer creates TXT record:{" "}
                      <code className="bg-blue-100 px-1 rounded">
                        asuid.help
                      </code>{" "}
                      &rarr; Azure verification hash (get from Azure Portal)
                    </li>
                    <li>
                      Add hostname in Azure Portal &rarr; App Service &rarr;
                      Custom domains
                    </li>
                    <li>Azure auto-provisions free SSL certificate</li>
                    <li>Set the domain in the field above</li>
                    <li>
                      Verify: visit{" "}
                      <code className="bg-blue-100 px-1 rounded">
                        https://help.customer.com/kb/
                      </code>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions (shown when no site loaded) */}
      {!status && !loading && (
        <div className="bg-slate-50 rounded-lg p-6 text-slate-600 mb-6">
          <h3 className="font-semibold text-slate-800 mb-2">How to use:</h3>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter a Site ID above</li>
            <li>
              Click &quot;Check &amp; Fix&quot; to verify the site configuration
            </li>
            <li>
              If tenant or subscription is missing, they will be created
              automatically
            </li>
            <li>Toggle feature flags as needed</li>
            <li>All changes are safe and only affect the specified site</li>
          </ol>
        </div>
      )}

      {/* ═══════════ KB Production Migration Panel ═══════════ */}
      <div className="mt-8 border-t-2 border-slate-200 pt-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-slate-800 mb-1 flex items-center gap-2">
            <i className="fa fa-database text-indigo-500" />
            KB Production Migration
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            Track migration progress and manage KB database connections
          </p>

          {/* Connection Status */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              Current KB Database Connection
            </h3>
            {connLoading ? (
              <LoadingSpinner />
            ) : connInfo?.configured ? (
              <div className="flex flex-col gap-3">
                <div
                  className={`p-4 rounded-lg border-2 ${
                    connInfo.current?.isAlternate
                      ? "bg-amber-50 border-amber-400"
                      : "bg-green-50 border-green-400"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`inline-block w-2 h-2 rounded-full ${
                            connInfo.current?.isAlternate
                              ? "bg-amber-500"
                              : "bg-green-500"
                          }`}
                        />
                        <span className="text-sm font-semibold">
                          Connected to:{" "}
                          <span className="font-mono">
                            {connInfo.current?.database}
                          </span>
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            connInfo.current?.isAlternate
                              ? "bg-amber-200 text-amber-800"
                              : "bg-green-200 text-green-800"
                          }`}
                        >
                          {connInfo.current?.isAlternate
                            ? "Staging DB"
                            : "Production DB"}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 font-mono">
                        {connInfo.current?.server}
                      </div>
                    </div>
                    {connInfo.hasAlternate && (
                      <SecondaryBlueButton
                        label={
                          swapping
                            ? "Switching..."
                            : connInfo.current?.isAlternate
                            ? "Switch to Production KB"
                            : "Switch to Staging KB"
                        }
                        onClick={() =>
                          swapConnection(!connInfo.current?.isAlternate)
                        }
                        disabled={swapping}
                      />
                    )}
                  </div>
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                    <strong>Persists across restarts.</strong> During migration, staging
                    points to prod KB so you can edit help articles before v20 is live.
                    Once v20 is deployed to production, switch staging back to its own KB
                    here. You can always flip back temporarily if you need to fix prod
                    KB data from staging.
                  </div>
                </div>

                {/* Show both connection options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div
                    className={`p-3 rounded-lg border ${
                      !connInfo.current?.isAlternate
                        ? "border-green-300 bg-green-50"
                        : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div className="text-xs text-slate-500 mb-1">
                      Primary (Production KB)
                    </div>
                    <div className="text-sm font-mono">
                      {connInfo.primary?.database}
                    </div>
                    <div className="text-xs text-slate-400 font-mono">
                      {connInfo.primary?.server}
                    </div>
                    {!connInfo.current?.isAlternate && (
                      <span className="text-xs text-green-600 font-medium">
                        Active
                      </span>
                    )}
                  </div>
                  {connInfo.hasAlternate && connInfo.alternate && (
                    <div
                      className={`p-3 rounded-lg border ${
                        connInfo.current?.isAlternate
                          ? "border-amber-300 bg-amber-50"
                          : "border-slate-200 bg-slate-50"
                      }`}
                    >
                      <div className="text-xs text-slate-500 mb-1">
                        Alternate (Staging KB)
                      </div>
                      <div className="text-sm font-mono">
                        {connInfo.alternate.database}
                      </div>
                      <div className="text-xs text-slate-400 font-mono">
                        {connInfo.alternate.server}
                      </div>
                      {connInfo.current?.isAlternate && (
                        <span className="text-xs text-amber-600 font-medium">
                          Active
                        </span>
                      )}
                    </div>
                  )}
                  {!connInfo.hasAlternate && (
                    <div className="p-3 rounded-lg border border-dashed border-slate-300 bg-slate-50">
                      <div className="text-xs text-slate-500 mb-1">
                        Alternate (Staging KB)
                      </div>
                      <div className="text-xs text-slate-400 italic">
                        Not configured. Set{" "}
                        <code className="bg-slate-200 px-1 rounded">
                          KnowledgeBaseConnectionString_Staging
                        </code>{" "}
                        app setting to enable switching.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                {connInfo?.error ??
                  "KB database not configured on this environment."}
              </div>
            )}
          </div>

          {/* Data Verification */}
          {migrationCounts?.configured && migrationCounts.counts && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">
                Current KB Data Counts
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {Object.entries(migrationCounts.counts).map(([key, count]) => (
                  <div
                    key={key}
                    className="p-3 bg-slate-50 rounded-lg text-center"
                  >
                    <div className="text-lg font-bold text-slate-800">
                      {(count as number).toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-500 capitalize">
                      {key}
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={fetchMigrationCounts}
                className="mt-2 text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                Refresh counts
              </button>
            </div>
          )}

          {/* Migration Checklist */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">
                Migration Checklist
              </h3>
              <span className="text-xs text-slate-500">
                {completedSteps}/{totalSteps} complete
              </span>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-slate-200 rounded-full h-2 mb-4">
              <div
                className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${(completedSteps / totalSteps) * 100}%`
                }}
              />
            </div>
            <div className="space-y-2">
              {MIGRATION_STEPS.map((step) => {
                const done = !!checklist[step.id];
                return (
                  <div
                    key={step.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                      done
                        ? "bg-slate-50 border-slate-200"
                        : "bg-white border-slate-300"
                    }`}
                  >
                    <div className="pt-0.5">
                      <Toggle
                        toggled={done}
                        changeToggle={() => toggleStep(step.id)}
                      />
                    </div>
                    <div className="flex-1">
                      <div
                        className={`text-sm ${
                          done
                            ? "text-slate-400 line-through"
                            : "text-slate-700"
                        }`}
                      >
                        {step.label}
                      </div>
                      {step.detail && !done && (
                        <div className="text-xs text-slate-400 mt-1 font-mono">
                          {step.detail}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* DNS Cutover Instructions */}
          <div className="border border-indigo-200 rounded-lg p-4 bg-indigo-50">
            <h3 className="text-sm font-semibold text-indigo-900 mb-3 flex items-center gap-2">
              <i className="fa fa-exchange text-indigo-500" />
              DNS Cutover for help.velaro.com
            </h3>
            <div className="space-y-3">
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="text-xs font-semibold text-red-800 mb-2">
                  Important: Hostname is currently bound to v10
                  (kb-us-east-prod). These 3 steps must happen together:
                </div>
                <ol className="text-xs text-red-700 space-y-1 list-decimal list-inside">
                  <li>
                    Remove{" "}
                    <code className="bg-red-100 px-1 rounded">
                      help.velaro.com
                    </code>{" "}
                    from{" "}
                    <code className="bg-red-100 px-1 rounded">
                      kb-us-east-prod
                    </code>{" "}
                    (Azure Portal &rarr; Custom domains &rarr; Delete)
                  </li>
                  <li>
                    Add{" "}
                    <code className="bg-red-100 px-1 rounded">
                      help.velaro.com
                    </code>{" "}
                    to{" "}
                    <code className="bg-red-100 px-1 rounded">
                      velaro-admin-prod
                    </code>{" "}
                    (Azure Portal &rarr; Custom domains &rarr; Add). TXT
                    verification record already exists.
                  </li>
                  <li>
                    Update CNAME in EasyDNS:{" "}
                    <code className="bg-red-100 px-1 rounded">help</code>{" "}
                    &rarr;{" "}
                    <code className="bg-red-100 px-1 rounded">
                      velaro-admin-prod.azurewebsites.net
                    </code>
                  </li>
                </ol>
              </div>
              <div className="p-3 bg-white rounded-lg border border-indigo-200">
                <div className="text-xs text-slate-500 mb-1">
                  Current DNS (v10 — old system)
                </div>
                <div className="text-sm font-mono">
                  CNAME: <strong>help</strong> &rarr;{" "}
                  <code className="bg-slate-100 px-1 rounded">
                    kb-us-east-prod.azurewebsites.net
                  </code>
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg border border-indigo-200">
                <div className="text-xs text-slate-500 mb-1">
                  Target DNS (v20 production)
                </div>
                <div className="text-sm font-mono">
                  CNAME: <strong>help</strong> &rarr;{" "}
                  <code className="bg-green-100 px-1 rounded">
                    velaro-admin-prod.azurewebsites.net
                  </code>
                </div>
              </div>
              <div className="text-xs text-indigo-700 space-y-1">
                <p>
                  <strong>TXT record:</strong>{" "}
                  <code className="bg-indigo-100 px-1 rounded">
                    asuid.help.velaro.com
                  </code>{" "}
                  already exists in EasyDNS &mdash; keep it permanently.
                </p>
                <p>
                  <strong>DNS Provider:</strong> EasyDNS (velaro.com zone)
                </p>
                <p>
                  <strong>After cutover:</strong> Set the Velaro KB tenant
                  Domain to{" "}
                  <code className="bg-indigo-100 px-1 rounded">
                    help.velaro.com
                  </code>{" "}
                  in the KB tenant record (use the domain field above or via
                  /kb/settings).
                </p>
                <p>
                  <strong>Verify:</strong> Visit help.velaro.com and check for{" "}
                  <code className="bg-indigo-100 px-1 rounded">v20</code> badge
                  in footer.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
